import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { fetchBhajanDataRequest, selectCatalog, selectStatus } from '../Features/BhajanSlice';
import type { AppDispatch } from '../store/store';
import { Card, EmptyState, Skeleton, cn } from '../ui';
import { bhajanToCardData } from './BhajanCard';
import { fetchNodes } from '../services/api';
import type { AnyGraphNode } from '../types/graphNodes';
import { titleCase } from '../utils/titleCase';

const COLLECTION_NODE_TYPE_ID = 21;
const BHAJAN_NODE_TYPE_ID = 1;

const Collections: React.FC = () => {
  const { nodeId } = useParams<{ nodeId?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const catalog = useSelector(selectCatalog);
  const status = useSelector(selectStatus);

  useEffect(() => {
    if (status === 'idle') dispatch(fetchBhajanDataRequest());
  }, [dispatch, status]);

  const allNodes = useMemo<AnyGraphNode[]>(
    () => (catalog?.nodes ?? []),
    [catalog]
  );

  const collections = useMemo(
    () => allNodes
      .filter((n) => n.nodeTypeId === COLLECTION_NODE_TYPE_ID)
      .sort((a, b) => {
        const an = (a.data?.normalizedName as string | undefined) || (a.data?.name as string | undefined) || '';
        const bn = (b.data?.normalizedName as string | undefined) || (b.data?.name as string | undefined) || '';
        return an.localeCompare(bn);
      }),
    [allNodes]
  );

  // The slim index omits COLLECTION `artUrl`; hydrate the (few) collection
  // nodes' full data for cover images. One batched call.
  const [artById, setArtById] = useState<Record<number, string>>({});
  useEffect(() => {
    if (collections.length === 0) return;
    let cancelled = false;
    fetchNodes(collections.map((c) => c.nodeId))
      .then((full) => {
        if (cancelled) return;
        const map: Record<number, string> = {};
        for (const node of full) {
          const url = node?.data?.artUrl;
          if (typeof url === 'string') map[node.nodeId] = url;
        }
        setArtById(map);
      })
      .catch(() => { /* art is optional — cards fall back to the gradient */ });
    return () => { cancelled = true; };
  }, [collections]);

  const isDetail = !!nodeId;
  const detail = useMemo(() => {
    if (!isDetail) return null;
    const n = allNodes.find((x) => x.nodeId === Number(nodeId) && x.nodeTypeId === COLLECTION_NODE_TYPE_ID);
    if (!n) return null;
    // Preserve edge ORDER — the collection has an ordered list of bhajans.
    const orderedIds = (n.edges ?? [])
      .filter((e) => e.name === 'bhajan' || e.name === 'bhajans' || e.name === 'track')
      .map((e) => e.nodeId);
    const byId = new Map(allNodes.filter((b) => b.nodeTypeId === BHAJAN_NODE_TYPE_ID).map((b) => [b.nodeId, b]));
    const bhajans = orderedIds
      .map((id) => byId.get(id))
      .filter((b): b is AnyGraphNode => !!b)
      .map((b) => bhajanToCardData(b, allNodes));
    const title = titleCase(
      (n.data?.normalizedName as string | undefined) ||
        (n.data?.name as string | undefined) ||
        `Collection ${n.nodeId}`,
    );
    const artUrl = artById[n.nodeId];
    return { node: n, title, bhajans, artUrl };
  }, [isDetail, nodeId, allNodes, artById]);

  const loading = status === 'loading' || (status === 'idle' && !catalog);

  // ============== DETAIL VIEW ==============
  if (isDetail) {
    if (!loading && !detail) {
      return (
        <main id="main">
          <EmptyState
            title="Collection not found"
            description="This collection doesn't exist or hasn't loaded yet."
            action={<Link to="/collections" className="text-accent-gold hover:underline">View all collections</Link>}
          />
        </main>
      );
    }
    return (
      <main id="main">
        <article className="mx-auto max-w-3xl px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
          <Link to="/collections" className="inline-block text-small text-text-tertiary hover:text-text-primary mb-4">
            ← All collections
          </Link>

          {/* Collection art picture (when available) */}
          {detail?.artUrl && (
            <div className="flex justify-center mb-6">
              <img
                src={detail.artUrl}
                alt=""
                className="rounded-2xl border border-light shadow-2 w-full max-w-xs object-contain bg-bg-2"
              />
            </div>
          )}

          <header className="mb-8 text-center">
            <h1 className="text-h1 sm:text-display font-display font-semibold leading-tight">
              {detail?.title ?? <Skeleton width={300} height={40} />}
            </h1>
            <p className="mt-2 text-text-secondary">
              {loading ? 'Loading…' : `${detail?.bhajans.length ?? 0} bhajan${detail?.bhajans.length === 1 ? '' : 's'}`}
            </p>
          </header>

          {/* Single-column ORDERED table — preserves the collection's order */}
          {loading ? (
            <div className="rounded-2xl border border-light bg-bg-2 overflow-hidden">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-light/40 last:border-b-0">
                  <Skeleton width={32} height={16} />
                  <Skeleton width={200} height={20} />
                </div>
              ))}
            </div>
          ) : detail && detail.bhajans.length > 0 ? (
            <ol className="rounded-2xl border border-light bg-bg-2 overflow-hidden divide-y divide-white/5" reversed={false}>
              {detail.bhajans.map((b, i) => (
                <li key={b.nodeId}>
                  <Link
                    to={`/nodes/${b.nodeId}`}
                    className="flex items-baseline gap-4 px-4 sm:px-5 py-3 hover:bg-bg-3/60 transition-colors group"
                  >
                    <span className="font-mono text-small text-text-tertiary tabular-nums w-8 shrink-0 text-right">
                      {i + 1}.
                    </span>
                    <span className="flex-1 min-w-0 font-display text-h3 truncate group-hover:text-accent-gold transition-colors">
                      {b.title}
                    </span>
                    <span className="hidden sm:flex items-center gap-2 text-small text-text-tertiary shrink-0">
                      {b.ragam && <span>{b.ragam}</span>}
                      {b.talam && <span>·</span>}
                      {b.talam && <span>{b.talam}</span>}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyState
              title="No bhajans in this collection"
              description="The collection has no linked bhajans yet."
            />
          )}
        </article>
      </main>
    );
  }

  // ============== LIST VIEW ==============
  return (
    <main id="main">
      <section
        aria-label="Collections"
        className="relative border-b border-light"
        style={{
          background:
            'radial-gradient(ellipse at 30% 0%, rgba(245,158,11,0.10) 0%, transparent 55%), radial-gradient(ellipse at 80% 30%, rgba(99,102,241,0.10) 0%, transparent 55%), var(--bg-1)',
        }}
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 sm:py-12 text-center">
          <h1 className="text-h1 font-display font-semibold mb-2">Collections</h1>
          <p className="text-text-secondary">
            {loading
              ? 'Loading…'
              : `${collections.length.toLocaleString()} curated collection${collections.length === 1 ? '' : 's'}`}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5">
            {Array.from({ length: 9 }).map((_, i) => (
              <CollectionCardSkeleton key={i} />
            ))}
          </div>
        ) : collections.length === 0 ? (
          <EmptyState
            title="No collections yet"
            description="Collections will appear here when added to the library."
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 sm:gap-5">
            {collections.map((c) => (
              <CollectionCard key={c.nodeId} node={c} artUrl={artById[c.nodeId]} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

const CollectionCard: React.FC<{ node: AnyGraphNode; artUrl?: string }> = ({ node, artUrl }) => {
  const title = titleCase(
    (node.data?.normalizedName as string | undefined) ||
      (node.data?.name as string | undefined) ||
      `Collection ${node.nodeId}`,
  );
  const bhajanCount = (node.edges ?? []).filter((e) => e.name === 'bhajan' || e.name === 'bhajans' || e.name === 'track').length;

  return (
    <Link to={`/collections/${node.nodeId}`} className="group block h-full focus-visible:outline-none">
      <Card
        interactive
        className={cn(
          'relative flex h-full w-full flex-col overflow-hidden transition-all duration-250 ease-out-quint',
          'hover:-translate-y-0.5 hover:shadow-3 hover:ring-1 hover:ring-accent-gold/40'
        )}
      >
        {/* Picture (if the collection has artUrl) — top of card */}
        {artUrl ? (
          // Square, not 16:9. Collection art is authored as a centred square emblem and
          // the app renders it 1:1 (AlbumArtwork, resizeMode cover). Cropping it to 16:9
          // here discarded 21.9% off the top and bottom — enough to cut the crescent moon
          // off Shivarathri and the coconut off the kalasha, i.e. exactly the elements
          // that identify each collection.
          <div className="aspect-square overflow-hidden bg-bg-3">
            <img
              src={artUrl}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-400 group-hover:scale-[1.03]"
            />
          </div>
        ) : (
          <div
            className="aspect-square flex items-center justify-center text-3xl"
            style={{
              background:
                'radial-gradient(ellipse at 30% 20%, rgba(124,58,237,0.18) 0%, transparent 55%), radial-gradient(ellipse at 80% 90%, rgba(99,102,241,0.18) 0%, transparent 55%), linear-gradient(135deg, #0a0e1a 0%, #1f2937 100%)',
            }}
            aria-hidden
          >
            📚
          </div>
        )}
        <div className="p-3 sm:p-4 relative">
          <span
            className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full opacity-50 group-hover:opacity-100 transition-opacity"
            style={{ background: 'linear-gradient(180deg, var(--accent-purple) 0%, var(--accent-indigo) 100%)' }}
            aria-hidden
          />
          <div className="flex items-start justify-between mb-1">
            <span className="font-mono text-small text-text-tertiary tabular-nums">#{node.nodeId}</span>
          </div>
          <h3 className="font-display text-body sm:text-h3 font-semibold text-text-primary line-clamp-2 mb-1">
            {title}
          </h3>
          <p className="text-small text-text-tertiary">
            {bhajanCount === 0 ? 'No bhajans yet' : `${bhajanCount} bhajan${bhajanCount === 1 ? '' : 's'}`}
          </p>
        </div>
      </Card>
    </Link>
  );
};

const CollectionCardSkeleton = () => (
  <Card className="w-full p-3 sm:p-4">
    <div className="flex items-start justify-between mb-3">
      <Skeleton width={50} height={14} />
      <Skeleton circle width={20} height={20} />
    </div>
    <Skeleton width="80%" height={26} />
    <Skeleton width="60%" height={14} className="mt-3" />
  </Card>
);

export default Collections;
